import { serializeCommentThread } from './commentMarkers';
import { escapeHtml } from './escapeHtml';
import { serializeInline } from './serializeInline';
import type { SerializeContext, SerializeOptions } from './serializeOptions';
import { LIST_ATTRIBUTE_NAMES } from '../list-attributes';
import type { CastInlineContent, CastParagraph } from '../types';

/**
 * Serializes a paragraph. Font metrics (family/size) are emitted as tag
 * attributes only when they *differ* from the section's primary style
 * (`sectionStyle`) — a paragraph that matches its section stays bare. Bold /
 * italic / underline are never tag attributes; they're wrapped inline by
 * serializeText.
 *
 * The comment threads that end in this paragraph follow it on their own lines,
 * outside the `<p>`, so a paragraph only ever holds document text.
 */
export function serializeParagraph(
  paragraph: CastParagraph,
  options: SerializeOptions,
  context: SerializeContext = {}
): string | null {
  if (paragraph.included !== true) return null;

  const { commentMarkers, sectionStyle } = context;
  const attrs: string[] = options.emitIds ? [`id="${escapeHtml(paragraph.id)}"`] : [];
  if (options.emitFinalText) attrs.push(`final="${escapeHtml(getParagraphRawText(paragraph))}"`);

  const style = paragraph.primaryStyle;
  if (
    options.emitFontFamily &&
    style?.fontFamily !== undefined &&
    style.fontFamily !== sectionStyle?.fontFamily
  ) {
    attrs.push(`font-family="${escapeHtml(style.fontFamily)}"`);
  }
  if (
    options.emitFontSize &&
    style?.fontSize !== undefined &&
    style.fontSize !== sectionStyle?.fontSize
  ) {
    attrs.push(`font-size="${style.fontSize}"`);
  }

  if (paragraph.listLevel !== undefined) {
    attrs.push(`${LIST_ATTRIBUTE_NAMES.listLevel}="${paragraph.listLevel}"`);
  }
  if (paragraph.listType !== undefined) {
    attrs.push(`${LIST_ATTRIBUTE_NAMES.listType}="${paragraph.listType}"`);
  }
  if (paragraph.listMarker !== undefined) {
    attrs.push(`${LIST_ATTRIBUTE_NAMES.listMarker}="${escapeHtml(paragraph.listMarker)}"`);
  }

  const inner = paragraph.children.map(child => serializeInline(child, options, context)).join('');
  const html = attrs.length ? `<p ${attrs.join(' ')}>${inner}</p>` : `<p>${inner}</p>`;

  const threads = commentMarkers?.threadsAfter.get(paragraph) ?? [];
  return [html, ...threads.map(serializeCommentThread)].join('\n');
}

/**
 * Builds the paragraph's text in its final / accepted state — the plain text a
 * reader would see if every tracked change were accepted. Runs carrying a
 * tracked deletion (`deleteAuthorId`) are dropped (even if they also carry an
 * insertion); insertion-only and plain runs are kept. Style metadata and
 * selection markers are ignored.
 *
 * This is exposed as the `final="..."` attribute so consumers — notably the
 * redline editor — have one unambiguous string to anchor on, rather than
 * having to reconstruct the current state from the inline `<ins>`/`<del>` runs.
 */
function getParagraphRawText(paragraph: CastParagraph): string {
  return inlineRawText(paragraph.children);
}

function inlineRawText(children: CastInlineContent[]): string {
  return children
    .map(child => {
      if (child.type === 'text') return child.deleteAuthorId === undefined ? child.value : '';
      if (child.type === 'link') return inlineRawText(child.children);
      return '';
    })
    .join('');
}
